import { useLocale as __useLocale } from "@/context/LocaleContext";
import { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { putServerFavorites } from "@/lib/api";
import api from "@/lib/api";
import { canPurchase } from "@/lib/inventory";

const StoreContext = createContext(null);

const read = (key, fallback) => {
  try {
    return JSON.parse(localStorage.getItem(key)) ?? fallback;
  } catch {
    return fallback;
  }
};

export function StoreProvider({ children }) {
  const { t: __t } = __useLocale();
  const { user } = useAuth();
  const [cart, setCart] = useState(() => read("shule-cart", []));
  const [favorites, setFavorites] = useState(() => read("shule-favorites", []));
  const [cartOpen, setCartOpen] = useState(false);

  useEffect(() => localStorage.setItem("shule-cart", JSON.stringify(cart)), [cart]);
  useEffect(() => localStorage.setItem("shule-favorites", JSON.stringify(favorites)), [favorites]);

  // Giriş yapınca sunucu favorileriyle birleştir
  useEffect(() => {
    if (!user) return;
    api.get("/auth/favorites").then((r) => {
      const server = r.data?.favorites || [];
      setFavorites((prev) => {
        const merged = Array.from(new Set([...prev, ...server]));
        if (merged.length !== prev.length || merged.length !== server.length) {
          putServerFavorites(merged).catch(() => {});
        }
        return merged;
      });
    }).catch(() => {});
  }, [user]);

  const addToCart = useCallback((product, variant, qty = 1, giftWrap = false, silent = false) => {
    if (!canPurchase(product)) {
      if (!silent) toast.error(__t("Bu ürün şu anda satın almaya kapalı."));
      return false;
    }
    const key = `${product.slug}|${variant?.id || "std"}|${giftWrap ? "gw" : "nw"}`;
    setCart((prev) => {
      const found = prev.find((i) => i.key === key);
      if (found) return prev.map((i) => (i.key === key ? { ...i, qty: i.qty + qty } : i));
      return [
        ...prev,
        {
          key,
          slug: product.slug,
          name: product.name,
          price: product.price,
          image: product.images?.[0],
          variant: variant?.label || "Standart",
          variant_id: variant?.id || "",
          checkout_provider: product.checkout_provider || "",
          giftWrap,
          qty,
        },
      ];
    });
    if (!silent) toast.success(__t(`${product.name} sepete eklendi`));
    return true;
  }, [__t]);

  const updateQty = useCallback((key, delta) => {
    setCart((prev) =>
      prev
        .map((i) => (i.key === key ? { ...i, qty: Math.max(0, i.qty + delta) } : i))
        .filter((i) => i.qty > 0)
    );
  }, []);

  const removeFromCart = useCallback((key) => {
    setCart((prev) => prev.filter((i) => i.key !== key));
  }, []);

  const clearCart = useCallback(() => setCart([]), []);

  const toggleFavorite = useCallback((slug) => {
    setFavorites((prev) => {
      const has = prev.includes(slug);
      const next = has ? prev.filter((s) => s !== slug) : [...prev, slug];
      if (!has) toast.success(__t("Favorilere eklendi"));
      if (user) putServerFavorites(next).catch(() => {});
      return next;
    });
  }, [user, __t]);

  const value = useMemo(() => {
    const cartCount = cart.reduce((s, i) => s + i.qty, 0);
    const subtotal = cart.reduce((s, i) => s + i.qty * i.price, 0);
    return {
      cart, cartCount, subtotal, addToCart, updateQty, removeFromCart, clearCart,
      favorites, toggleFavorite, cartOpen, setCartOpen,
    };
  }, [cart, favorites, cartOpen, addToCart, updateQty, removeFromCart, clearCart, toggleFavorite]);

  return <StoreContext.Provider value={value}>{children}</StoreContext.Provider>;
}

export const useStore = () => useContext(StoreContext);
